/* eslint-disable react/jsx-indent, react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import useWindowSize from '../../hooks/useWindowSize';

import { useEdition } from '../../contexts/EditionContext';
import FiltersButton from '../buttons/FiltersButton';
import ActiveFiltersButtons from '../partials/ActiveFiltersButtons';
import Filters from '../partials/Filters';
import NoResults from '../partials/NoResults';
// import Tag from '../partials/Tag';
import CardsList from './CardsList';
import FilmCarousel from './FilmCarousel';
import QueryList from './QueryList';

import styles from '../../../styles/lists/index-list.module.scss';

const propTypes = {
    repositories: PropTypes.arrayOf(PropTypes.string),
    header: PropTypes.node,
    filters: PropTypes.array, // eslint-disable-line react/forbid-prop-types
    query: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    onQueryChange: PropTypes.func,
    onQueryReset: PropTypes.func,
    className: PropTypes.string,
    itemClassName: PropTypes.string,
    isDark: PropTypes.bool,
};

const defaultProps = {
    repositories: ['films', 'events', 'articles'],
    header: null,
    filters: null,
    query: {},
    onQueryChange: null,
    onQueryReset: null,
    className: null,
    itemClassName: null,
    isDark: false,
};

const SearchIndexList = ({
    repositories,
    header,
    filters,
    query,
    onQueryChange,
    onQueryReset,
    className,
    itemClassName,
    isDark,
}) => {
    const ref = useRef();
    const countRef = useRef({});
    const edition = useEdition();

    const { width: windowWidth } = useWindowSize();
    const [countByList, setCountByList] = useState({});

    const [filtersOpened, setFiltersOpened] = useState(false);
    const toggleFilters = useCallback(
        (e) => {
            e.preventDefault();
            setFiltersOpened(!filtersOpened);
        },
        [filtersOpened, setFiltersOpened],
    );

    useEffect(() => {
        document.body.style.overflow = filtersOpened ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [filtersOpened]);

    useEffect(() => {
        setFiltersOpened(false);
    }, [filters, windowWidth]);

    const onItemsCountChange = useCallback(
        (listCount, name) => {
            countRef.current = { ...countRef.current, [name]: listCount };
            setCountByList(countRef.current);
        },
        [countRef, setCountByList],
    );

    const fullCount = useMemo(
        () => Object.keys(countByList).reduce((sum, key) => sum + (countByList[key] || 0), 0),
        [countByList],
    );

    const queryWithEdition = useMemo(() => {
        const { edition: queryEdition = null } = query || {};
        if (queryEdition === null) {
            return { edition: 'all', ...query };
        }
        return query;
    }, [query, edition]);

    const items = {
        films: {
            label: <FormattedMessage defaultMessage="Films" description="Search list title" />,
            count: 10,
            query: queryWithEdition,
        },
        events: {
            label: <FormattedMessage defaultMessage="Calendar" description="Search list title" />,
            count: 5,
            query: queryWithEdition,
        },
        articles: {
            label: <FormattedMessage defaultMessage="Articles" description="Search list title" />,
            count: 5,
            query: queryWithEdition,
        },
        pages: {
            label: <FormattedMessage defaultMessage="Festival" description="Search list title" />,
            count: 5,
            query,
        },
    };

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.filtersOpened]: filtersOpened,
                    [styles.noResults]: fullCount === 0,
                },
            ])}
        >
            <div className={styles.inner}>
                <aside className={styles.sidePanel}>
                    <div className={styles.sidePanelInner} ref={ref}>
                        <Filters
                            className={styles.sidePanelFilters}
                            innerClassName={styles.sidePanelFields}
                            isOpenClassName={styles.filterIsOpen}
                            itemWrapperClassName={styles.filterItemWrapper}
                            filters={filters}
                            query={query}
                            onChange={onQueryChange}
                            onApply={toggleFilters}
                            onReset={onQueryReset}
                        />
                    </div>
                </aside>
                <main className={styles.main}>
                    {header}
                    <ActiveFiltersButtons
                        className={styles.activeFilters}
                        filters={filters}
                        query={query}
                        onChange={onQueryChange}
                        onReset={onQueryReset}
                        isDark={isDark}
                    />
                    {(repositories || []).map((repository) => (
                        <div
                            key={`repo-${repository}`}
                            className={classNames([
                                styles.repository,
                                {
                                    [itemClassName]: itemClassName !== null,
                                },
                            ])}
                        >
                            <QueryList
                                title={items[repository] ? items[repository].label : null}
                                repository={repository}
                                query={items[repository] ? items[repository].query : query}
                                count={items[repository] ? items[repository].count : null}
                                listComponent={repository === 'films' ? FilmCarousel : CardsList}
                                listProps={{ cardProps: { withYear: true } }}
                                isDark
                                onItemsCountChange={onItemsCountChange}
                                clearOnEmpty
                            />
                        </div>
                    ))}
                    {/* {fullCount === 0 ? (
                        <div className={styles.emptyElement}>
                            <NoResults isDark={isDark} />
                        </div>
                    ) : null} */}
                    <FiltersButton
                        className={styles.filtersBtn}
                        opened={filtersOpened}
                        onClick={toggleFilters}
                    />
                </main>
            </div>
        </div>
    );
};

SearchIndexList.propTypes = propTypes;
SearchIndexList.defaultProps = defaultProps;

export default SearchIndexList;
